const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export interface RequestOptions extends RequestInit {
  params?: Record<string, any>;
  data?: any;
  responseType?: 'json' | 'blob';
}

export class ApiError<T = any> extends Error {
  status: number
  data?: T

  constructor(message: string, status: number, data?: T) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

export async function apiClient<T>(endpoint: string, { params, data, ...options }: RequestOptions = {}): Promise<T> {
  const url = endpoint.startsWith('http')
    ? new URL(endpoint)
    : new URL(`${BASE_URL}${endpoint}`, window.location.origin)

  if (params) {
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        if (Array.isArray(params[key])) {
          params[key].forEach((v: any) => url.searchParams.append(key, String(v)))
        } else {
          url.searchParams.append(key, String(params[key]))
        }
      }
    })
  }

  const headers = new Headers(options.headers)
  if (data && !(data instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const token = localStorage.getItem('token')
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(url.toString(), {
    ...options,
    headers,
    body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
  })

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token')
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    const errorBody = await response.json().catch(() => ({}))
    const message = errorBody.Message || errorBody.message || `Error ${response.status}: ${response.statusText}`
    throw new ApiError(message, response.status, errorBody)
  }

  if (response.status === 204) {
    return {} as T
  }

  if (options.responseType === 'blob') {
    return response.blob() as unknown as T
  }

  return response.json()
}

export const api = {
  get: <T>(url: string, params?: any, options?: RequestOptions) =>
    apiClient<T>(url, { ...options, method: 'GET', params }),
  post: <T>(url: string, data?: any, options?: RequestOptions) =>
    apiClient<T>(url, { ...options, method: 'POST', data }),
  put: <T>(url: string, data?: any, options?: RequestOptions) =>
    apiClient<T>(url, { ...options, method: 'PUT', data }),
  patch: <T>(url: string, data?: any, options?: RequestOptions) =>
    apiClient<T>(url, { ...options, method: 'PATCH', data }),
  delete: <T>(url: string, options?: RequestOptions) =>
    apiClient<T>(url, { ...options, method: 'DELETE' }),
}
